import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { authenticatedHeadersHash } from "views_shared/app/javascript/scripts/authentication";
import RelativeTime from "./RelativeTime";

// PublishCountdown — under-badge text describing when a scheduled
// StoryIdea publishes.
//
// Three render states:
//   1. publish_at in the future  → RelativeTime ("in 5m", "in 2h",
//      "in 3d") with the absolute publish time on hover. Coarse
//      buckets (per-second MM:SS is distracting at index scale).
//   2. publish_at has just passed → "Publishing…" while we wait for
//      Sidekiq to actually run the job. Polls PostgREST via
//      onStatusFlip until the row's status flips to 'published'.
//   3. publish_at passed >STALE_AFTER_MS ago without a flip →
//      "Publish attempt failed — contact engineering".
//
// RelativeTime owns its own re-tick so the future-state branch always
// shows fresh text without a page refresh. The other branches re-render
// on the local `now` tick below, which also drives the poll-window
// transition into state 3.

const TICK_MS = 30_000; // half-minute granularity; nothing finer is visible
const POLL_INTERVAL_MS = 2500;
const POLL_MAX_WAIT_MS = 30_000;
const STALE_AFTER_MS = 5 * 60_000;

function PublishCountdown({ publishAt, onStatusFlip }) {
  const publishAtMs = useMemo(() => (publishAt ? new Date(publishAt).getTime() : null), [publishAt]);

  const [now, setNow] = useState(() => Date.now());

  // pollStartedAt captures the moment we entered the "Publishing…"
  // window so we can fall back to the stale-scheduled rendering
  // after POLL_MAX_WAIT_MS without a status flip.
  const pollStartedAtRef = useRef(null);
  const [pollAbandoned, setPollAbandoned] = useState(false);

  // Re-tick. TICK_MS is intentionally coarse — half a minute is
  // imperceptible for the relative text but cheap.
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), TICK_MS);
    return () => window.clearInterval(t);
  }, []);

  useEffect(() => {
    if (!publishAtMs || !onStatusFlip) return undefined;
    if (now < publishAtMs) {
      pollStartedAtRef.current = null;
      if (pollAbandoned) setPollAbandoned(false);
      return undefined;
    }
    if (pollStartedAtRef.current == null) {
      pollStartedAtRef.current = Date.now();
    }
    const elapsed = Date.now() - pollStartedAtRef.current;
    if (elapsed > POLL_MAX_WAIT_MS) {
      if (!pollAbandoned) setPollAbandoned(true);
      return undefined;
    }
    const handle = window.setTimeout(() => {
      onStatusFlip();
    }, POLL_INTERVAL_MS);
    return () => window.clearTimeout(handle);
  }, [publishAtMs, now, onStatusFlip, pollAbandoned]);

  if (!publishAtMs) {
    return null;
  }

  const remaining = publishAtMs - now;
  const overdueMs = -remaining;

  if (remaining > 0) {
    return (
      <small className="text-muted">
        <RelativeTime date={publishAt} />
      </small>
    );
  }

  // The red "Publish attempt failed" state only fires when the row has
  // been overdue for more than STALE_AFTER_MS (5 min). The shorter
  // POLL_MAX_WAIT_MS (30s) just stops the client-side polling — at that
  // point we stop bothering the API and fall back to the "Publishing…"
  // spinner until the row is genuinely stale. StuckPublishSnapshotJob
  // is the authoritative ops signal at ~10 min; the UI shouldn't cry
  // wolf much earlier than that.
  if (overdueMs > STALE_AFTER_MS) {
    return (
      <small className="text-danger" title="Sidekiq did not run the job within the expected window.">
        <i className="fa-solid fa-triangle-exclamation me-1" aria-hidden="true" />
        Publish attempt failed
      </small>
    );
  }

  return (
    <small className="text-muted">
      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
      Publishing&hellip;
    </small>
  );
}

PublishCountdown.propTypes = {
  publishAt: PropTypes.string, // ISO8601 PostgREST format
  // Invoked roughly every POLL_INTERVAL_MS during the "Publishing…"
  // window. Parent refetches the row's status; if it flipped to
  // 'published', the parent re-renders with the new status and this
  // component unmounts.
  onStatusFlip: PropTypes.func,
};

PublishCountdown.defaultProps = {
  publishAt: null,
  onStatusFlip: null,
};

// Exposed for callers that want to poll the show endpoint themselves
// (e.g. a standalone show page rather than the index table).
PublishCountdown.fetchStoryIdeaStatus = async function fetchStoryIdeaStatus(storyIdeaId) {
  const endpoint = `${globalThis.Qwoted.SUPABASE_API_EXTERNAL_URL}/rest/v1/story_ideas`;
  const url = `${endpoint}?id=eq.${storyIdeaId}&select=status,published_at`;
  const resp = await fetch(url, { headers: authenticatedHeadersHash() });
  if (!resp.ok) return null;
  const rows = await resp.json();
  return rows[0] || null;
};

export default PublishCountdown;
